
import { isPublicSharePath } from '@/constants/public-share-routes';
import { redirectToLogin } from '@/lib/auth-return';

// Global reference to auth functions (set when authorizedFetch is first called)
let authLogout: (() => void) | null = null;
let authPromptLogin: (() => void) | null = null;

/** In-memory token mirror — kept in sync with localStorage by AuthProvider. */
let authAccessToken: string | null = null;

/** Update JWT storage atomically (React state updated separately in use-auth). */
export function setAuthAccessToken(token: string | null): void {
  authAccessToken = token;
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  }
}

export function getAuthAccessToken(): string | null {
  if (authAccessToken) return authAccessToken;
  const stored = localStorage.getItem('auth_token');
  authAccessToken = stored;
  return stored;
}

/**
 * Authorized fetch wrapper that handles authentication headers and 401 responses
 */
export async function authorizedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  // Get auth context (this needs to be called within a React component)
  // We'll set up the global references when this is first called
  if (!authLogout || !authPromptLogin) {
    throw new Error('authorizedFetch must be used within an AuthProvider context');
  }

  const token = getAuthAccessToken();

  // Add authorization header if token exists
  const headers = new Headers(options.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // Avoid setting Content-Type on GET/HEAD (no body): it triggers a CORS preflight on cross-origin calls.
  if (
    !headers.has('Content-Type') &&
    options.body != null &&
    !(options.body instanceof FormData)
  ) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized responses — only logout when the active session token was rejected.
  if (response.status === 401 && token && getAuthAccessToken() === token) {
    console.log('Received 401 response, clearing auth state and redirecting to login');
    authLogout?.();
    const path = window.location.pathname;
    if (!isPublicSharePath(path) && path !== '/' && !path.startsWith('/login')) {
      redirectToLogin(`${path}${window.location.search}`)
    }
  }

  return response;
}

/** Parse JSON or text API error bodies into a user-visible message. */
export async function parseApiErrorMessage(
  response: Response,
  fallback: string,
): Promise<string> {
  const text = await response.text().catch(() => '')
  const trimmed = text.trim()
  if (!trimmed) {
    return `${fallback} (${response.status})`
  }
  try {
    const data = JSON.parse(trimmed) as Record<string, unknown>
    if (typeof data.error === 'string' && data.error) return data.error
    const det = data.detail
    if (typeof det === 'string' && det) return det
    if (Array.isArray(det) && det.length > 0) {
      const first = det[0] as Record<string, unknown>
      if (typeof first.msg === 'string' && first.msg) return first.msg
      if (typeof first.message === 'string' && first.message) return first.message
    }
  } catch {
    /* not JSON */
  }
  return trimmed.length > 300 ? `${trimmed.slice(0, 297)}…` : trimmed
}

/**
 * Initialize authorizedFetch with auth context functions
 * This should be called once when the app starts, within the AuthProvider
 */
export function initializeAuthorizedFetch(logout: () => void, promptLogin: () => void) {
  authLogout = logout;
  authPromptLogin = promptLogin;
}
